/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// DepletionsChart.jsx — ported from the source app (logic verbatim; only the
// CSS import is module-local). Recharts line / stacked-bar of case-sales,
// grouped by the chosen dimension.
import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Label, LabelList,
} from "recharts";
import "./di-chart.css";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const MONTH_ABBR = MONTHS.map(m => m.slice(0, 3));

const COLORS = [
  "#f5a623","#3b82f6","#4eca8d","#e8526a","#a78bfa",
  "#fb923c","#22d3ee","#84cc16","#f472b6","#facc15",
  "#60a5fa","#34d399","#f87171","#c084fc","#fbbf24",
];

// Axis / grid / label colors retuned for Brand Command's dark-first theme.
// recharts writes these as SVG presentation attributes (a CSS var wouldn't
// resolve), so use concrete values that read on BOTH the dark (navy) and light
// (white) chart backgrounds — not pure white, which would vanish in light mode.
const AXIS_TEXT = "var(--muted)";  // theme chrome — follows light/dark
const GRID_LINE = "rgba(140,160,190,0.18)";
const AXIS_LINE = "rgba(140,160,190,0.28)";

// Custom legend that auto-flows into 2 columns when there are >10 series
// (e.g. by state). Long lists no longer extend below the chart bounds —
// the wrapper has its own maxHeight + overflow set in wrapperStyle.
function CompactLegend({ payload, chartType }) {
  if (!payload?.length) return null;
  const useTwoCols = payload.length > 10;
  return (
    <ul style={{
      listStyle: "none",
      margin: 0,
      padding: 0,
      columnCount: useTwoCols ? 2 : 1,
      columnGap: 14,
    }}>
      {payload.map((entry, i) => (
        <li key={`${entry.value}-${i}`} style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 0",
          breakInside: "avoid",
          fontSize: 12,
          color: AXIS_TEXT,
          lineHeight: "20px",
        }}>
          <span style={{
            display: "inline-block",
            width: chartType === "bar" ? 11 : 16,
            height: chartType === "bar" ? 11 : 3,
            background: entry.color,
            borderRadius: chartType === "bar" ? 2 : 0,
            flexShrink: 0,
          }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entry.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

function getSeriesLabel(r, groupBy) {
  switch (groupBy) {
    case "region":      return r.states?.region       || "Unknown";
    case "state":       return r.states?.state_name   || "Unknown";
    case "distributor": return r.states?.distributor  || "Unknown";
    case "flavor":      return r.flavors?.name        || "Unknown";
    case "size":        return r.sizes?.name          || "Unknown";
    case "pkg":         return r.pack_sizes?.name     || "Unknown";
    default: {
      const f = r.flavors?.name;
      const s = r.sizes?.name;
      if (f && s) return `${f} ${s}`;
      return f || s || "Unknown";
    }
  }
}

export default function DepletionsChart({ rows, groupBy = "flavor_size" }) {
  const [chartType, setChartType] = useState("bar");

  const { chartData, seriesKeys } = useMemo(() => {
    if (!rows.length) return { chartData: [], seriesKeys: [] };

    const seriesLabel = r => getSeriesLabel(r, groupBy);

    const seriesSet = new Set();
    rows.forEach(r => seriesSet.add(seriesLabel(r)));
    const series = [...seriesSet].sort();

    // Use year-month keys so multi-year data renders correctly
    const colKey = (year, month) => `${year}-${String(month).padStart(2, "0")}`;
    const colKeySet = new Set();
    rows.forEach(r => colKeySet.add(colKey(r.year, r.month)));
    const sortedKeys = [...colKeySet].sort();

    const acc = {};
    rows.forEach(r => {
      const ck  = colKey(r.year, r.month);
      const key = seriesLabel(r);
      if (!acc[ck]) acc[ck] = {};
      acc[ck][key] = (acc[ck][key] ?? 0) + (Number(r.case_sales) || 0);
    });

    const multiYear = new Set(rows.map(r => r.year)).size > 1;
    const chartData = sortedKeys.map(ck => {
      const [y, m] = ck.split("-");
      const label  = multiYear
        ? `${MONTH_ABBR[Number(m) - 1]} ${String(y).slice(2)}`
        : MONTH_ABBR[Number(m) - 1];
      const row = { month: label };
      series.forEach(s => { row[s] = acc[ck]?.[s] ?? 0; });
      return row;
    });

    const activeSeries = series.filter(s => chartData.some(row => (row[s] ?? 0) > 0));

    // Pre-compute bar totals so labels don't rely on Recharts index (which can be offset when last series has 0-value months)
    chartData.forEach(row => {
      row._total = activeSeries.reduce((sum, s) => sum + (row[s] ?? 0), 0);
    });

    return { chartData, seriesKeys: activeSeries };
  }, [rows, groupBy]);

  if (!rows.length) {
    return (
      <div className="chart-prompt">
        <div className="chart-prompt-icon">📊</div>
        <div className="chart-prompt-text">No depletion data for the current filters.</div>
      </div>
    );
  }

  const hasData = chartData.some(row => seriesKeys.some(k => row[k] > 0));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-label">{label}</div>
        <div className="chart-tooltip-total">Total: <strong>{Math.round(total).toLocaleString()}</strong> cases</div>
        <div className="chart-tooltip-divider" />
        {[...payload].reverse().map(p => {
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) + "%" : "—";
          return (
            <div key={p.dataKey} className="chart-tooltip-row">
              <span className="chart-tooltip-swatch" style={{ background: p.color }} />
              <span className="chart-tooltip-name">{p.dataKey}</span>
              <span className="chart-tooltip-value">{p.value > 0 ? Math.round(p.value).toLocaleString() : "—"}</span>
              <span className="chart-tooltip-pct">{p.value > 0 ? pct : ""}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Label rendered above each stacked bar showing the full-stack total
  // Uses value prop (= row._total from LabelList) to avoid Recharts index-offset bug on 0-value months
  const BarTotalLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    const txt = value >= 1000 ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k` : Math.round(value).toLocaleString();
    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle" fill={AXIS_TEXT} fontSize={11} fontWeight={600}>
        {txt}
      </text>
    );
  };

  const yFmt = v => v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v);
  const sharedProps = { data: chartData, margin: { top: 28, right: 16, left: 8, bottom: 4 } };

  const xAxis = <XAxis dataKey="month" tick={{ fill: AXIS_TEXT, fontSize: 12 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />;
  const yAxis = (
    <YAxis tickFormatter={yFmt} tick={{ fill: AXIS_TEXT, fontSize: 11 }} axisLine={false} tickLine={false} width={64}>
      <Label value="Cases" angle={-90} position="insideLeft" offset={12}
        style={{ fill: AXIS_TEXT, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }} />
    </YAxis>
  );
  const grid    = <CartesianGrid strokeDasharray="3 3" stroke={GRID_LINE} vertical={false} />;
  const tooltip = <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(232,93,38,0.10)" }} />;
  const legend  = (
    <Legend layout="vertical" align="right" verticalAlign="middle"
      wrapperStyle={{ fontSize: 12, color: AXIS_TEXT, paddingLeft: 16, lineHeight: "22px",
        maxHeight: "92%", overflowY: "auto", top: "4%" }}
      content={<CompactLegend chartType={chartType} />} />
  );

  return (
    <div className="chart-wrap">
      <div className="chart-controls">
        <div className="chart-type-toggle">
          <button
            className={`chart-type-btn${chartType === "line" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("line")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Line
          </button>
          <button
            className={`chart-type-btn${chartType === "bar" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("bar")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="4" height="18"/><rect x="10" y="8" width="4" height="13"/><rect x="17" y="13" width="4" height="8"/>
            </svg>
            Stacked Bar
          </button>
        </div>
        {!hasData && <span className="chart-no-data">No data for current filters</span>}
      </div>

      <div className="chart-area">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart {...sharedProps}>
              {grid}{xAxis}{yAxis}{tooltip}{legend}
              {seriesKeys.map((sku, i) => (
                <Line key={sku} type="linear" dataKey={sku}
                  stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                  dot={{ r: 3, fill: COLORS[i % COLORS.length], strokeWidth: 0 }}
                  activeDot={{ r: 5 }} connectNulls />
              ))}
            </LineChart>
          ) : (
            <BarChart {...sharedProps}>
              {grid}{xAxis}{yAxis}{tooltip}{legend}
              {seriesKeys.map((sku, i) => (
                <Bar key={sku} dataKey={sku} stackId="stack"
                  fill={COLORS[i % COLORS.length]}
                  radius={i === seriesKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}>
                  {i === seriesKeys.length - 1 && (
                    <LabelList dataKey="_total" content={<BarTotalLabel />} />
                  )}
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
